/** Sentinel leadId for hiring resume viewers (annotations/markup in shared collections). */
export const HIRING_VIEWER_LEAD_ID = '__hiring__';

const HIRING_RESUME_DOCUMENT_RE = /^hiring:([^:]+):resume$/;

/** Stable document key for a job-application resume (survives Storage URL token refresh). */
export function buildHiringResumeDocumentId(applicationId: string): string {
  return `hiring:${applicationId}:resume`;
}

export function parseHiringResumeApplicationId(documentId?: string | null): string | null {
  const match = HIRING_RESUME_DOCUMENT_RE.exec((documentId || '').trim());
  return match?.[1] || null;
}

export function isHiringResumeDocumentId(documentId?: string | null): boolean {
  return parseHiringResumeApplicationId(documentId) != null;
}

export function isHiringResumeLeadId(leadId?: string | null): boolean {
  return (leadId || '').trim() === HIRING_VIEWER_LEAD_ID;
}

export function isHiringResumePdf(resume?: { fileName?: string; contentType?: string } | null): boolean {
  const name = (resume?.fileName || '').toLowerCase();
  const contentType = (resume?.contentType || '').toLowerCase().split(';')[0].trim();
  return contentType === 'application/pdf' || name.endsWith('.pdf');
}

export function formatResumeFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function buildHiringCandidatePath(params: {
  applicationId?: string;
  teamCommentId?: string;
  resume?: boolean;
}): string {
  const query = new URLSearchParams();
  query.set('tab', 'candidates');
  if (params.applicationId?.trim()) query.set('application', params.applicationId.trim());
  if (params.teamCommentId?.trim()) query.set('teamComment', params.teamCommentId.trim());
  if (params.resume) query.set('resume', '1');
  return `/people/hiring?${query.toString()}`;
}

export function isHiringCandidateNotificationLink(link?: string | null): boolean {
  const path = (link || '').trim();
  if (!path.startsWith('/people/hiring')) return false;
  return path.includes('tab=candidates') || path.includes('teamComment=');
}

export function buildHiringResumeViewerPath(params: {
  applicationId: string;
  annotationId?: string;
  pageNumber?: number;
}): string {
  const query = new URLSearchParams();
  query.set('tab', 'candidates');
  query.set('application', params.applicationId);
  query.set('resume', '1');
  if (params.annotationId?.trim()) query.set('comment', params.annotationId.trim());
  if (params.pageNumber != null && !Number.isNaN(Number(params.pageNumber))) {
    query.set('page', String(params.pageNumber));
  }
  return `/people/hiring?${query.toString()}`;
}

export function hiringResumeViewerPathFromNotification(notification: {
  documentId?: string;
  leadId?: string;
  sourceId?: string;
  pdfPageNumber?: number;
}): string | null {
  const applicationId = parseHiringResumeApplicationId(notification.documentId);
  if (!applicationId) {
    return isHiringResumeLeadId(notification.leadId) ? '/people/hiring' : null;
  }
  return buildHiringResumeViewerPath({
    applicationId,
    annotationId: notification.sourceId,
    pageNumber: notification.pdfPageNumber,
  });
}
